import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { vendorService } from '../../../services/api';
import { ChevronDown, Search, X } from 'lucide-react';
import DateRangePicker from '../../../components/DateRangePicker';

const parseCustomDateTime = (dateStr) => {
  if (!dateStr) return null;
  // Match DD-MM-YYYY HH:MM:SS AM/PM (or single digits)
  const match = dateStr.match(/^(\d{1,2})-(\d{1,2})-(\d{4})\s+(\d{1,2}):(\d{1,2}):(\d{1,2})\s*(AM|PM)?$/i);
  if (match) {
    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1; // 0-indexed month
    const year = parseInt(match[3], 10);
    let hours = parseInt(match[4], 10);
    const minutes = parseInt(match[5], 10);
    const seconds = parseInt(match[6], 10);
    const ampm = match[7];

    if (ampm) {
      if (ampm.toUpperCase() === 'PM' && hours < 12) {
        hours += 12;
      } else if (ampm.toUpperCase() === 'AM' && hours === 12) {
        hours = 0;
      }
    }
    return new Date(year, month, day, hours, minutes, seconds);
  }
  
  // Try standard parsing as fallback
  const d = new Date(dateStr);
  if (!isNaN(d.getTime())) {
    return d;
  }
  return null;
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    const d = parseCustomDateTime(dateStr);
    if (!d || isNaN(d.getTime())) return 'N/A';
    const pad = (num) => String(num).padStart(2, '0');
    const day = pad(d.getDate());
    const month = pad(d.getMonth() + 1);
    const year = d.getFullYear();
    const hours = pad(d.getHours());
    const minutes = pad(d.getMinutes());
    return `${day}/${month}/${year}, ${hours}:${minutes}`;
  } catch (err) {
    return 'N/A';
  }
};

const getLeadStatusColorClass = (status) => {
  const s = (status || 'New').toLowerCase();
  switch (s) {
    case 'new':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'hot':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'warm':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'cold':
      return 'bg-slate-50 text-slate-600 border-slate-200';
    case 'lost':
      return 'bg-rose-50 text-rose-700 border-rose-200';
    case 'architect/designer':
      return 'bg-purple-50 text-purple-700 border-purple-200';
    default:
      return 'bg-blue-50 text-blue-700 border-blue-200';
  }
};

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const base = isLocal ? 'http://localhost:8080' : '';
  if (url.startsWith('/listings/') || url.startsWith('/static/')) return `${base}${url}`;
  return `${base}/listings${url.startsWith('/') ? '' : '/'}${url}`;
};

const getPageNumbers = (currentPage, totalPages) => {
  const pages = [];
  const maxVisiblePages = 5;

  if (totalPages <= maxVisiblePages) {
    for (let i = 0; i < totalPages; i++) {
      pages.push(i);
    }
  } else {
    pages.push(0);

    let start = Math.max(1, currentPage - 1);
    let end = Math.min(totalPages - 2, currentPage + 1);

    if (currentPage <= 1) {
      end = 3;
    } else if (currentPage >= totalPages - 2) {
      start = totalPages - 4;
    }

    if (start > 1) {
      pages.push('ellipsis1');
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages - 2) {
      pages.push('ellipsis2');
    }

    pages.push(totalPages - 1);
  }
  return pages;
};

const ListingApprovalsTab = ({ toast, setLightboxImage }) => {
  const { hasPermission } = useAuth();

  // Tab State
  const [listingSubTab, setListingSubTab] = useState('new'); // 'new', 'updated', 'all', 'no_listing'
  
  // Search & Filter
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchContainerRef = useRef(null);

  const [listingFilter, setListingFilter] = useState('');
  const [listingTypeFilter, setListingTypeFilter] = useState('');
  const [leadStatusFilter, setLeadStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [categoriesList, setCategoriesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);
  const [listingFilters, setListingFilters] = useState({ startDate: '', endDate: '' });

  // Pagination States
  const [listingPage, setListingPage] = useState(0);
  const [listingPageSize] = useState(10);
  const [usersNoListingPageSize, setUsersNoListingPageSize] = useState(25);

  // Data States
  const [listings, setListings] = useState([]);
  const [usersNoListing, setUsersNoListing] = useState([]);
  const [listingTotalPages, setListingTotalPages] = useState(0);
  const [listingTotalItems, setListingTotalItems] = useState(0);

  // UI States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modals States
  const [selectedListing, setSelectedListing] = useState(null);
  const [selectedGovtProofListing, setSelectedGovtProofListing] = useState(null);
  const [rejectListingId, setRejectListingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  // Debounced search query update
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
      setListingPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Click outside to close search suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Compute Autocomplete Suggestions based on active tab
  const getSuggestions = () => {
    if (!searchInput.trim()) return [];
    const q = searchInput.toLowerCase().trim();
    if (listingSubTab === 'no_listing') {
      return usersNoListing
        .filter(u =>
          (u.name && u.name.toLowerCase().includes(q)) ||
          (u.emailId && u.emailId.toLowerCase().includes(q)) ||
          (u.mobileNo && u.mobileNo.toLowerCase().includes(q))
        )
        .slice(0, 6)
        .map(u => ({ label: u.name || u.emailId, subLabel: u.emailId }));
    } else {
      return listings
        .filter(l =>
          (l.companyName && l.companyName.toLowerCase().includes(q)) ||
          (l.email && l.email.toLowerCase().includes(q)) ||
          (l.phone && l.phone.toLowerCase().includes(q))
        )
        .slice(0, 6)
        .map(l => ({ label: l.companyName, subLabel: `${l.email || ''} ${l.city ? '• ' + l.city : ''}`.trim() }));
    }
  };
  const suggestionsList = getSuggestions();

  // Fetch Categories & Cities for filters on mount
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const catRes = await vendorService.getCategories();
        if (catRes.success && catRes.data) {
          setCategoriesList(catRes.data || []);
        }
      } catch (err) {
        console.error("Failed to load categories for filter", err);
      }
      try {
        const cityRes = await vendorService.getAdminListingCities();
        if (cityRes.success && cityRes.data && cityRes.data.cities) {
          setCitiesList(cityRes.data.cities || []);
        }
      } catch (err) {
        console.error("Failed to load cities for filter", err);
      }
    };
    loadFilterOptions();
  }, []);

  // Fetch Listings Function
  const fetchListings = useCallback(async () => {
    if (!hasPermission('APPROVE_LISTINGS')) return;
    setLoading(true);
    setError('');

    try {
      if (listingSubTab === 'no_listing') {
        const res = await vendorService.getUsersNoListing({
          page: listingPage,
          size: usersNoListingPageSize,
          search: searchQuery,
          leadStatus: leadStatusFilter,
          startDate: listingFilters.startDate,
          endDate: listingFilters.endDate
        });
        if (res.success && res.data) {
          if (Array.isArray(res.data)) {
            setUsersNoListing(res.data);
            setListingTotalItems(res.data.length);
            setListingTotalPages(Math.ceil(res.data.length / usersNoListingPageSize));
          } else {
            setUsersNoListing(res.data.users || []);
            setListingTotalPages(res.data.totalPages || 0);
            setListingTotalItems(res.data.totalItems || 0);
          }
        } else {
          setError(res.error || 'Failed to fetch registered users without listings');
        }
        setLoading(false);
        return;
      }

      let statusParam = listingFilter;
      let isUpdatedParam = null;

      if (listingSubTab === 'new') {
        statusParam = 'PENDING';
        isUpdatedParam = false;
      } else if (listingSubTab === 'updated') {
        statusParam = 'PENDING';
        isUpdatedParam = true;
      }

      const res = await vendorService.getAdminListings(
        statusParam,
        searchQuery,
        listingPage,
        listingPageSize,
        isUpdatedParam,
        null,
        null,
        listingFilters.startDate,
        listingFilters.endDate,
        categoryFilter,
        cityFilter,
        listingTypeFilter
      );
      if (res.success && res.data) {
        setListings(res.data.listings || []);
        setListingTotalPages(res.data.totalPages || 0);
        setListingTotalItems(res.data.totalItems || 0);
      } else {
        setError(res.error || 'Failed to fetch listings');
      }
    } catch (err) {
      setError('An error occurred while fetching listings.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [hasPermission, listingSubTab, listingFilter, searchQuery, listingPage, listingPageSize, usersNoListingPageSize, leadStatusFilter, listingFilters.startDate, listingFilters.endDate, categoryFilter, cityFilter, listingTypeFilter]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  // Actions
  const handleUpdateListingStatus = async (id, status, rejectReasonMessage = null) => {
    setError('');
    try {
      const res = await vendorService.updateListingStatus(id, status, rejectReasonMessage);
      if (res.success) {
        toast.success(`Listing status updated to ${status.toLowerCase()} successfully.`);
        fetchListings();
      } else {
        toast.error("Failed to update status: " + res.error);
      }
    } catch (err) {
      toast.error("An error occurred while updating listing status.");
      console.error(err);
    }
  };

  const submitRejection = async () => {
    if (!rejectReason.trim()) {
      toast.warning("Please provide a rejection reason.");
      return;
    }
    await handleUpdateListingStatus(rejectListingId, 'REJECTED', rejectReason);
    setRejectListingId(null);
    setRejectReason('');
  };

  const handleLeadStatusChange = async (vendorEmail, newStatus) => {
    try {
      const res = await vendorService.updateLeadStatus(vendorEmail, newStatus);
      if (res.success) {
        toast.success(`Successfully updated lead status to ${newStatus}`);
        setUsersNoListing(prevUsers =>
          prevUsers.map(u =>
            u.emailId === vendorEmail ? { ...u, lead_status: newStatus } : u
          )
        );
      } else {
        toast.error(res.error || 'Failed to update lead status');
      }
    } catch (err) {
      toast.error('An error occurred while updating lead status');
      console.error(err);
    }
  };

  // Server handles filtering & pagination for users without listings


  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden text-left">
      {/* Sub-tab Navigation */}
      <div className="flex border-b border-gray-200 px-6 pt-4 bg-white gap-6">
        {[
          { id: 'new', label: 'New Listings Approvals' },
          { id: 'updated', label: 'Updated Listings Approvals' },
          { id: 'all', label: 'All Listings' },
          { id: 'no_listing', label: 'Not Listed Yet' }
        ].map(sub => (
          <button
            key={sub.id}
            onClick={() => {
              setListingSubTab(sub.id);
              setListingPage(0);
              setSearchInput('');
              setSearchQuery('');
              setLeadStatusFilter('');
              setCategoryFilter('');
              setCityFilter('');
            }}
            className={`pb-3 text-sm font-bold border-b-2 transition-all cursor-pointer ${listingSubTab === sub.id
              ? 'border-[#2ab391] text-[#2ab391]'
              : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
          >
            {sub.label}
          </button>
        ))}
      </div>

      {/* Search & Filter Header */}
      <div className="p-5 border-b border-gray-100 bg-gray-50/50 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Real-time Autocomplete Searchbar with internal Clear X Icon */}
          <div className="relative min-w-[280px] sm:min-w-[340px] max-w-md flex-1" ref={searchContainerRef}>
            <div className="relative flex items-center">
              <Search size={18} className="text-gray-400 absolute left-3.5 pointer-events-none" />
              <input
                type="text"
                placeholder={listingSubTab === 'no_listing' ? "Search by name, email or mobile..." : "Search by company name..."}
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                className="w-full bg-white border border-gray-200 rounded-lg py-2 pl-10 pr-9 text-sm focus:border-[#2ab391] focus:ring-1 focus:ring-[#2ab391] focus:outline-none shadow-xs transition-colors font-medium text-gray-700 placeholder-gray-400"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchInput('');
                    setSearchQuery('');
                    setShowSuggestions(false);
                    setListingPage(0);
                  }}
                  className="absolute right-3 text-gray-400 hover:text-gray-600 transition-colors p-0.5 rounded-full hover:bg-gray-100 cursor-pointer"
                  title="Clear search filter"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Autocomplete Suggestions Dropdown */}
            {showSuggestions && searchInput.trim() !== '' && suggestionsList.length > 0 && (
              <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-100 rounded-xl shadow-lg max-h-60 overflow-y-auto py-1 text-left">
                {suggestionsList.map((item, idx) => (
                  <div
                    key={idx}
                    onClick={() => {
                      setSearchInput(item.label);
                      setSearchQuery(item.label);
                      setShowSuggestions(false);
                      setListingPage(0);
                    }}
                    className="px-4 py-2 hover:bg-teal-50/60 cursor-pointer transition-colors border-b border-gray-50 last:border-0 flex justify-between items-center"
                  >
                    <span className="text-sm font-semibold text-gray-800">{item.label}</span>
                    {item.subLabel && <span className="text-xs font-normal text-gray-400 ml-2">{item.subLabel}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Date Filter */}
          <div className="flex items-center gap-2 bg-gray-50 border border-gray-100 p-1.5 px-3 rounded-xl select-none">
            <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">DATE:</span>
            <DateRangePicker
              startDate={listingFilters.startDate}
              endDate={listingFilters.endDate}
              onChange={(start, end) => {
                setListingFilters({ startDate: start, endDate: end });
                setListingPage(0);
              }}
              placeholder="Select Date Range"
              className="!w-48 text-xs"
            />
            {(listingFilters.startDate || listingFilters.endDate) && (
              <button
                onClick={() => {
                  setListingFilters({ startDate: '', endDate: '' });
                  setListingPage(0);
                }}
                className="px-2 py-0.5 text-xs font-semibold text-red-650 hover:bg-red-50 border border-red-200/50 rounded-lg transition-colors cursor-pointer"
              >
                Clear
              </button>
            )}
          </div>

          {/* Category & City Filters for Listing Tabs (New, Updated, All) */}
          {listingSubTab !== 'no_listing' && (
            <>
              {/* Category Filter */}
              <div className="flex items-center gap-2 whitespace-nowrap">
                <span className="text-xs font-semibold text-gray-550 uppercase tracking-wider">Category:</span>
                <select
                  value={categoryFilter}
                  onChange={(e) => {
                    setCategoryFilter(e.target.value);
                    setListingPage(0);
                  }}
                  className="bg-white border border-gray-200 hover:border-gray-300 rounded-lg py-2 px-3 text-xs font-semibold text-gray-655 focus:border-[#2ab391] focus:ring-1 focus:ring-[#2ab391] focus:outline-none cursor-pointer shadow-sm transition-colors max-w-[180px] truncate"
                >
                  <option value="">All Categories</option>
                  {categoriesList.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* City Filter */}
              <div className="flex items-center gap-2 whitespace-nowrap">
                <span className="text-xs font-semibold text-gray-550 uppercase tracking-wider">City:</span>
                <select
                  value={cityFilter}
                  onChange={(e) => {
                    setCityFilter(e.target.value);
                    setListingPage(0);
                  }}
                  className="bg-white border border-gray-200 hover:border-gray-300 rounded-lg py-2 px-3 text-xs font-semibold text-gray-655 focus:border-[#2ab391] focus:ring-1 focus:ring-[#2ab391] focus:outline-none cursor-pointer shadow-sm transition-colors max-w-[150px] truncate"
                >
                  <option value="">All Cities</option>
                  {citiesList.map(city => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                </select>
              </div>

              {/* Listing Type Filter */}
              <div className="flex items-center gap-2 whitespace-nowrap">
                <span className="text-xs font-semibold text-gray-550 uppercase tracking-wider">Type:</span>
                <select
                  value={listingTypeFilter}
                  onChange={(e) => {
                    setListingTypeFilter(e.target.value);
                    setListingPage(0);
                  }}
                  className="bg-white border border-gray-200 hover:border-gray-300 rounded-lg py-2 px-3 text-xs font-semibold text-gray-655 focus:border-[#2ab391] focus:ring-1 focus:ring-[#2ab391] focus:outline-none cursor-pointer shadow-sm transition-colors"
                >
                  <option value="">All Types</option>
                  <option value="COMPANY">Company Listing</option>
                  <option value="INDIVIDUAL">Individual Business</option>
                </select>
              </div>
            </>
          )}

          {/* Lead Status Filter for 'no_listing' users */}
          {listingSubTab === 'no_listing' && (
            <div className="flex items-center gap-2 whitespace-nowrap">
              <span className="text-xs font-semibold text-gray-550 uppercase tracking-wider">Lead Status:</span>
              <select
                value={leadStatusFilter}
                onChange={(e) => {
                  setLeadStatusFilter(e.target.value);
                  setListingPage(0);
                }}
                className="bg-white border border-gray-200 hover:border-gray-300 rounded-lg py-2 px-3 text-xs font-semibold text-gray-655 focus:border-[#2ab391] focus:ring-1 focus:ring-[#2ab391] focus:outline-none cursor-pointer shadow-sm transition-colors"
              >
                <option value="">All Lead Statuses</option>
                <option value="New">New</option>
                <option value="Hot">Hot</option>
                <option value="Warm">Warm</option>
                <option value="Cold">Cold</option>
                <option value="Lost">Lost</option>
                <option value="Architect/Designer">Architect/Designer</option>
              </select>
            </div>
          )}

          {/* Status Filter for 'all' listings */}
          {listingSubTab === 'all' && (
            <div className="flex items-center gap-2 whitespace-nowrap">
              <span className="text-xs font-semibold text-gray-550 uppercase tracking-wider">Status Filter:</span>
              <select
                value={listingFilter}
                onChange={(e) => {
                  setListingFilter(e.target.value);
                  setListingPage(0);
                }}
                className="bg-white border border-gray-200 hover:border-gray-300 rounded-lg py-2 px-3 text-xs font-semibold text-gray-655 focus:border-[#2ab391] focus:ring-1 focus:ring-[#2ab391] focus:outline-none cursor-pointer shadow-sm transition-colors"
              >
                <option value="">All Statuses</option>
                <option value="PENDING">Pending Approvals</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
                <option value="SUSPENDED">Suspended</option>
              </select>
            </div>
          )}
        </div>

        {/* Category Badges & Stats */}
        <div className="flex items-center justify-between select-none">
          <div>
            {listingSubTab === 'new' && (
              <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2.5 py-1 rounded-lg border border-orange-100">
                Category: New Listings
              </span>
            )}
            {listingSubTab === 'updated' && (
              <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-lg border border-indigo-100">
                Category: Updated Listings
              </span>
            )}
            {listingSubTab === 'no_listing' && (
              <span className="text-xs font-bold text-[#2ab391] bg-teal-50 px-2.5 py-1 rounded-lg border border-teal-100">
                Category: Users Without Listing
              </span>
            )}
          </div>
          <span className="text-xs text-gray-500 font-bold bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-xl whitespace-nowrap">
            {listingSubTab === 'no_listing'
              ? `Total users: ${listingTotalItems}`
              : `Total listings: ${listingTotalItems}`}
          </span>
        </div>
      </div>

      {/* Loading Indicator */}
      {loading ? (
        <div className="flex flex-col justify-center items-center py-24 select-none">
          <div className="w-9 h-9 border-4 border-[#2ab391] border-t-transparent rounded-full animate-spin"></div>
          <span className="ml-3 text-sm text-gray-500 font-semibold mt-3">Loading records...</span>
        </div>
      ) : (
        <>
          {error && (
            <div className="bg-red-50 text-red-700 border border-red-200/50 p-4 rounded-xl m-6 flex items-center gap-3 shadow-sm">
              <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span className="text-sm font-medium">{error}</span>
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] border-collapse">
              {listingSubTab === 'no_listing' ? (
                <>
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider text-left">
                      <th className="p-4 pl-6">User Name</th>
                      <th className="p-4">Email ID</th>
                      <th className="p-4">Mobile No</th>
                      <th className="p-4">Lead Status</th>
                      <th className="p-4 pr-6">Registration Date & Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-sm text-gray-600">
                    {usersNoListing.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="p-12 text-center text-gray-400 font-semibold">
                          No registered users without listings found.
                        </td>
                      </tr>
                    ) : (
                      usersNoListing.map(u => (
                        <tr key={u.id} className="hover:bg-gray-50/50 transition-all">
                          <td className="p-4 pl-6 font-semibold text-gray-800">{u.name}</td>
                          <td className="p-4 font-medium text-gray-700">{u.emailId}</td>
                          <td className="p-4">{u.mobileNo}</td>
                          <td className="p-4">
                            <select
                              value={u.lead_status || 'New'}
                              onChange={(e) => handleLeadStatusChange(u.emailId, e.target.value)}
                              className={`border rounded-lg px-2 py-1 text-xs font-bold focus:outline-none focus:ring-1 focus:ring-[#2ab391] cursor-pointer transition-colors ${getLeadStatusColorClass(u.lead_status)}`}
                            >
                              <option value="New" className="bg-blue-50 text-blue-700 font-bold">New</option>
                              <option value="Hot" className="bg-emerald-50 text-emerald-700 font-bold">Hot</option>
                              <option value="Warm" className="bg-amber-50 text-amber-700 font-bold">Warm</option>
                              <option value="Cold" className="bg-slate-50 text-slate-600 font-bold">Cold</option>
                              <option value="Lost" className="bg-rose-50 text-rose-700 font-bold">Lost</option>
                              <option value="Architect/Designer" className="bg-purple-50 text-purple-700 font-bold">Architect/Designer</option>
                            </select>
                          </td>
                          <td className="p-4 pr-6 text-xs font-semibold text-gray-700 whitespace-nowrap">
                            {formatDateTime(u.createdAt)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </>
              ) : (
                <>
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider text-left">
                      <th className="p-4 pl-6">Company / Name</th>
                      <th className="p-4">Type</th>
                      <th className="p-4">Category</th>
                      <th className="p-4">Contact</th>
                      <th className="p-4">Location</th>
                      <th className="p-4">Status</th>
                      <th className="p-4">Date & Time</th>
                      <th className="p-4 pr-6 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-sm text-gray-600">
                    {listings.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="p-12 text-center text-gray-400 font-semibold">
                          No listings match the current status or search filter.
                        </td>
                      </tr>
                    ) : (
                      listings.map(l => (
                        <tr key={l.id} className="hover:bg-gray-50/50 transition-all">
                          <td className="p-4 pl-6 font-semibold text-gray-800">
                            <div>{l.companyName}</div>
                          </td>
                          <td className="p-4">
                            <span className={`px-2 py-0.5 text-xs font-bold rounded border whitespace-nowrap ${
                              (l.listingType || 'COMPANY').toUpperCase() === 'INDIVIDUAL'
                                ? 'bg-purple-50 text-purple-700 border-purple-200'
                                : 'bg-blue-50 text-blue-700 border-blue-200'
                            }`}>
                              {(l.listingType || 'COMPANY').toUpperCase() === 'INDIVIDUAL' ? 'Individual' : 'Company'}
                            </span>
                          </td>
                          <td className="p-4">
                            <span className="px-2 py-0.5 text-xs font-bold bg-[#eaf7f4] text-[#2ab391] rounded border border-teal-100/55 whitespace-nowrap">
                              {l.categoryName || 'Uncategorized'}
                            </span>
                          </td>
                          <td className="p-4">
                            <div>{l.email}</div>
                            <div className="text-xs text-gray-400 mt-0.5">{l.phone}</div>
                          </td>
                          <td className="p-4">{l.city}, {l.state}</td>
                          <td className="p-4">
                            <span className={`px-2.5 py-1 text-xs font-bold rounded-full uppercase ${l.status === 'APPROVED' ? 'bg-emerald-50 text-emerald-700' :
                              l.status === 'PENDING' ? 'bg-orange-50 text-orange-700' :
                                l.status === 'SUSPENDED' ? 'bg-rose-50 text-rose-700' :
                                  l.status === 'REJECTED' ? 'bg-red-50 text-red-700 font-bold' :
                                    'bg-gray-100 text-gray-600'
                              }`}>
                              {l.status}
                            </span>
                          </td>
                          <td className="p-4 text-xs font-semibold text-gray-700 whitespace-nowrap">
                            {formatDateTime(listingSubTab === 'updated' ? l.updatedAt : l.createdAt)}
                          </td>
                          <td className="p-4 pr-6 text-right text-left">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => setSelectedListing(l)}
                                className="text-gray-600 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 border border-gray-200 py-1.5 px-3 rounded-lg text-xs font-bold transition-all flex items-center gap-1 active:scale-95 cursor-pointer shadow-2xs"
                              >
                                View
                              </button>
                              {(listingSubTab === 'new' || listingSubTab === 'updated' || listingSubTab === 'all') && l.govtProofUrl && (
                                <button
                                  onClick={() => setSelectedGovtProofListing(l)}
                                  className="text-[#2ab391] hover:text-white hover:bg-[#2ab391] bg-white border border-[#2ab391] py-1.5 px-3 rounded-lg text-xs font-bold transition-all flex items-center gap-1 active:scale-95 cursor-pointer shadow-2xs whitespace-nowrap"
                                >
                                  View Proof
                                </button>
                              )}
                              <select
                                value={l.status}
                                onChange={(e) => {
                                  const nextStatus = e.target.value;
                                  if (nextStatus === 'REJECTED') {
                                    setRejectListingId(l.id);
                                    setRejectReason('');
                                  } else {
                                    handleUpdateListingStatus(l.id, nextStatus);
                                  }
                                }}
                                className="bg-white border border-gray-200 hover:border-gray-300 rounded-lg py-1.5 px-2.5 text-xs font-bold text-gray-600 focus:border-[#2ab391] focus:outline-none cursor-pointer"
                              >
                                <option value="PENDING">Pending</option>
                                <option value="APPROVED">Approve</option>
                                <option value="REJECTED">Reject</option>
                                <option value="SUSPENDED">Suspend</option>
                              </select>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </>
              )}
            </table>
          </div>

          {/* Pagination footer */}
          {listingSubTab === 'no_listing' ? (
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between text-xs text-gray-500 font-medium border-t border-gray-100 pt-5 mt-6 px-6 pb-6 select-none">
              <div className="text-gray-500 text-xs font-semibold text-center md:text-left w-full md:w-auto">
                {listingTotalItems === 0 ? (
                  'No records available'
                ) : (
                  `Showing ${listingPage * usersNoListingPageSize + 1} to ${Math.min((listingPage + 1) * usersNoListingPageSize, listingTotalItems)} of ${listingTotalItems} entries`
                )}
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 w-full md:w-auto">
                <div className="flex items-center justify-center gap-2 w-full sm:w-auto">
                  <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Show</span>
                  <div className="relative">
                    <select
                      value={usersNoListingPageSize}
                      onChange={(e) => {
                        setUsersNoListingPageSize(Number(e.target.value));
                        setListingPage(0);
                      }}
                      className="w-16 bg-white border border-gray-200 rounded-xl pl-3 pr-8 py-1.5 text-xs font-bold text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#2ab391] focus:border-[#2ab391] appearance-none cursor-pointer shadow-xs"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                    <span className="absolute inset-y-0 right-0 pr-2.5 flex items-center pointer-events-none text-gray-400">
                      <ChevronDown size={12} />
                    </span>
                  </div>
                  <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Entries</span>
                </div>

                {listingTotalPages > 0 && (
                  <div className="flex items-center justify-between sm:justify-start gap-1 w-full sm:w-auto">
                    <button
                      type="button"
                      onClick={() => setListingPage(p => Math.max(0, p - 1))}
                      disabled={listingPage === 0}
                      className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-xs flex-1 sm:flex-initial text-center"
                    >
                      Previous
                    </button>

                    <div className="hidden sm:flex items-center gap-1">
                      {getPageNumbers(listingPage, listingTotalPages).map((pageNum, idx) => {
                        if (pageNum === 'ellipsis1' || pageNum === 'ellipsis2') {
                          return (
                            <span key={`ell-${idx}`} className="px-2 py-1.5 text-gray-400 font-bold">
                              ...
                            </span>
                          );
                        }
                        const isActive = listingPage === pageNum;
                        return (
                          <button
                            key={`page-${pageNum}`}
                            type="button"
                            onClick={() => setListingPage(pageNum)}
                            className={`min-w-8 h-8 px-2.5 flex items-center justify-center rounded-xl text-xs font-bold border transition-all cursor-pointer ${isActive
                              ? 'bg-[#2ab391] border-[#2ab391] text-white shadow-sm'
                              : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                              }`}
                          >
                            {pageNum + 1}
                          </button>
                        );
                      })}
                    </div>

                    <span className="sm:hidden text-xs text-gray-500 font-bold px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl shadow-xs min-w-[70px] text-center">
                      {listingPage + 1} / {listingTotalPages}
                    </span>

                    <button
                      type="button"
                      onClick={() => setListingPage(p => Math.min(listingTotalPages - 1, p + 1))}
                      disabled={listingPage >= listingTotalPages - 1}
                      className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-xs flex-1 sm:flex-initial text-center"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between text-xs text-gray-500 font-medium border-t border-gray-100 pt-5 mt-6 px-6 pb-6 select-none">
              <div className="text-gray-500 text-xs font-semibold">
                {listingTotalItems === 0 ? (
                  'No records available'
                ) : (
                  `Showing ${listingPage * listingPageSize + 1} to ${Math.min((listingPage + 1) * listingPageSize, listingTotalItems)} of ${listingTotalItems} entries`
                )}
              </div>

              {listingTotalPages > 0 && (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setListingPage(p => Math.max(0, p - 1))}
                    disabled={listingPage === 0}
                    className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-xs"
                  >
                    Previous
                  </button>

                  <div className="hidden sm:flex items-center gap-1">
                    {getPageNumbers(listingPage, listingTotalPages).map((pageNum, idx) => {
                      if (pageNum === 'ellipsis1' || pageNum === 'ellipsis2') {
                        return (
                          <span key={`ell-${idx}`} className="px-2 py-1.5 text-gray-400 font-bold">
                            ...
                          </span>
                        );
                      }
                      const isActive = listingPage === pageNum;
                      return (
                        <button
                          key={`page-${pageNum}`}
                          type="button"
                          onClick={() => setListingPage(pageNum)}
                          className={`min-w-8 h-8 px-2.5 flex items-center justify-center rounded-xl text-xs font-bold border transition-all cursor-pointer ${isActive
                            ? 'bg-[#2ab391] border-[#2ab391] text-white shadow-sm'
                            : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                          {pageNum + 1}
                        </button>
                      );
                    })}
                  </div>

                  <span className="sm:hidden text-xs text-gray-500 font-bold px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl shadow-xs min-w-[70px] text-center">
                    {listingPage + 1} / {listingTotalPages}
                  </span>

                  <button
                    type="button"
                    onClick={() => setListingPage(p => Math.min(listingTotalPages - 1, p + 1))}
                    disabled={listingPage >= listingTotalPages - 1}
                    className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-xs"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Rejection Reason Modal */}
      {rejectListingId && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white max-w-md w-full rounded-2xl shadow-xl overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150 text-left">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <div>
                <span className="text-[10px] uppercase font-bold text-red-600 tracking-wider">Reject Listing</span>
                <h3 className="text-lg font-bold text-gray-800 mt-1">Specify Rejection Reason</h3>
              </div>
              <button onClick={() => setRejectListingId(null)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Message to Vendor</label>
              <textarea
                placeholder="Please describe why this listing is being rejected (e.g., invalid phone, logo mismatch, missing address)..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#2ab391] focus:border-[#2ab391] bg-white transition-all resize-none h-32 mb-6"
              />
              <div className="flex justify-end gap-2.5">
                <button
                  onClick={() => setRejectListingId(null)}
                  className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50 active:scale-[0.98] transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={submitRejection}
                  className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold active:scale-[0.98] transition-all"
                >
                  Submit Rejection
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Govt Certificate / GST Proof Modal */}
      {selectedGovtProofListing && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 overflow-y-auto p-4 md:p-6 flex justify-center items-start">
          <div className="bg-white max-w-4xl w-full rounded-2xl shadow-xl overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150 my-8 md:my-12 relative flex flex-col">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-slate-50">
              <div>
                <h3 className="text-base font-extrabold text-gray-800">
                  Government Certificate / GST Photo Proof Verification
                </h3>
                <p className="text-xs text-gray-500 mt-1 font-medium">
                  Verify details for: <strong className="text-gray-700">{selectedGovtProofListing.companyName}</strong> 
                  {selectedGovtProofListing.gstin && (
                    <> (GSTIN: <strong className="text-gray-700">{selectedGovtProofListing.gstin}</strong>)</>
                  )}
                </p>
              </div>
              <button
                onClick={() => setSelectedGovtProofListing(null)}
                className="bg-gray-100 hover:bg-gray-200 text-gray-600 p-2 rounded-full transition-all cursor-pointer"
                title="Close Verification"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="mx-6 mt-4 p-4 bg-orange-50/50 border border-orange-100 rounded-xl flex items-start gap-2.5">
              <span className="inline-block w-4 h-4 rounded-full bg-orange-500 text-white text-[10px] font-extrabold flex items-center justify-center flex-shrink-0 mt-0.5">!</span>
              <div className="text-xs text-orange-800 leading-relaxed">
                <strong>Verification Instructions:</strong> Please carefully review the document below. Ensure the company name (<strong>{selectedGovtProofListing.companyName}</strong>) or the GSTIN (<strong>{selectedGovtProofListing.gstin}</strong>) printed on the certificate corresponds to the registered business before changing the status to Approved.
              </div>
            </div>
            <div className="p-6 flex justify-center items-center bg-gray-50/50 min-h-[400px]">
              {selectedGovtProofListing.govtProofUrl && selectedGovtProofListing.govtProofUrl.toLowerCase().endsWith('.pdf') ? (
                <iframe 
                  src={`${getImageUrl(selectedGovtProofListing.govtProofUrl)}#toolbar=0`}
                  title="Government Certificate PDF Document"
                  className="w-full h-[600px] border border-gray-200 rounded-lg shadow-inner bg-white"
                />
              ) : (
                <img 
                  src={getImageUrl(selectedGovtProofListing.govtProofUrl)}
                  alt="Government Certificate Proof"
                  className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-md border border-gray-200"
                />
              )}
            </div>
            <div className="p-4 bg-slate-50 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setSelectedGovtProofListing(null)}
                className="px-5 py-2 border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 text-sm font-semibold rounded-lg shadow-sm active:scale-[0.98] transition-all cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Listing Details Modal */}
      {selectedListing && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 overflow-y-auto p-4 md:p-6 flex justify-center items-start">
          <div className="bg-white max-w-3xl w-full rounded-2xl shadow-xl overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150 my-8 md:my-12 relative flex flex-col">
            <div className="relative h-64 md:h-80 bg-slate-950 flex-shrink-0 flex items-center justify-center">
              {selectedListing.profileBannerUrl ? (
                <>
                  <div className="absolute inset-0 overflow-hidden">
                    <img
                      src={getImageUrl(selectedListing.profileBannerUrl)}
                      alt="Listing Banner Blur"
                      className="w-full h-full object-cover filter blur-lg opacity-40 select-none pointer-events-none scale-105"
                    />
                  </div>
                  <img
                    src={getImageUrl(selectedListing.profileBannerUrl)}
                    alt="Listing Banner"
                    className="relative max-w-full max-h-full object-contain z-10"
                  />
                </>
              ) : (
                <div className="w-full h-full bg-gradient-to-r from-[#2ab391] to-emerald-700"></div>
              )}
              <button
                onClick={() => setSelectedListing(null)}
                className="absolute top-4 right-4 bg-slate-900/60 hover:bg-slate-900/80 text-white p-2 rounded-full transition-all cursor-pointer z-20"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="absolute -bottom-8 left-6 z-20">
                <div className="w-20 h-20 rounded-xl bg-white border-2 border-white shadow-md p-0.5 overflow-hidden">
                  {selectedListing.logoUrl ? (
                    <img
                      src={getImageUrl(selectedListing.logoUrl)}
                      alt="Company Logo"
                      className="w-full h-full object-contain rounded-lg"
                    />
                  ) : (
                    <div className="w-full h-full bg-slate-100 flex items-center justify-center text-slate-450 font-bold text-xl select-none">
                      {selectedListing.companyName ? selectedListing.companyName.charAt(0).toUpperCase() : 'C'}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="pt-10 px-6 pb-4 border-b border-gray-100 flex justify-between items-start flex-wrap gap-3 text-left">
              <div>
                <h3 className="text-xl font-bold text-gray-800">{selectedListing.companyName}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs font-bold text-[#2ab391] bg-[#eaf7f4] px-2 py-0.5 rounded uppercase whitespace-nowrap">
                    {selectedListing.categoryName || 'Uncategorized'}
                  </span>
                  <span className="text-xs text-gray-400 font-medium">
                    Estd {selectedListing.establishedSince || 'N/A'}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 text-xs font-bold rounded-full uppercase ${selectedListing.status === 'APPROVED' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' :
                  selectedListing.status === 'PENDING' ? 'bg-orange-50 text-orange-700 border border-orange-100' :
                    selectedListing.status === 'SUSPENDED' ? 'bg-rose-50 text-rose-700 border border-rose-100' :
                      selectedListing.status === 'REJECTED' ? 'bg-red-50 text-red-700 border border-red-100' :
                        'bg-gray-100 text-gray-600'
                  }`}>
                  {selectedListing.status}
                </span>
              </div>
            </div>

            <div className="p-6 text-left space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2 space-y-6">
                  <div>
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">About Company</h4>
                    <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 p-4 rounded-xl border border-gray-150/50 whitespace-pre-wrap font-medium">
                      {selectedListing.aboutCompany || 'No description provided.'}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Search Tags</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedListing.tags && selectedListing.tags.length > 0 ? (
                        selectedListing.tags.map(tag => (
                          <span key={tag.id} className="px-2.5 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-full border border-gray-200/50 select-none">
                            {tag.name}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-gray-400 italic font-medium">No tags specified</span>
                      )}
                    </div>
                  </div>

                  {selectedListing.galleryImageUrls && selectedListing.galleryImageUrls.length > 0 && (
                    <div>
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Gallery Images</h4>
                      <div className="grid grid-cols-3 gap-3">
                        {selectedListing.galleryImageUrls.map((img, idx) => (
                          <div
                            key={idx}
                            onClick={() => setLightboxImage(getImageUrl(img))}
                            className="aspect-square rounded-xl overflow-hidden border border-gray-155 hover:border-[#2ab391] p-0.5 bg-white transition-all cursor-pointer flex items-center justify-center relative group shadow-2xs"
                          >
                            <img
                              src={getImageUrl(img)}
                              alt={`gallery-${idx}`}
                              className="w-full h-full object-cover rounded-lg"
                            />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="md:col-span-1 space-y-6">
                  <div>
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Contact Details</h4>
                    <div className="space-y-3 bg-gray-50/50 p-4 rounded-xl border border-gray-150/40 text-xs">
                      <div>
                        <span className="block text-[10px] text-gray-400 uppercase font-bold">Email Address</span>
                        {selectedListing.email ? (
                          <a href={`mailto:${selectedListing.email}`} className="text-[#2ab391] hover:underline font-bold break-all">
                            {selectedListing.email}
                          </a>
                        ) : (
                          <span className="text-gray-400 font-semibold">N/A</span>
                        )}
                      </div>
                      <div>
                        <span className="block text-[10px] text-gray-400 uppercase font-bold">Phone Number</span>
                        <span className="text-gray-800 font-bold">{selectedListing.phone || 'N/A'}</span>
                      </div>
                      {selectedListing.website && (
                        <div>
                          <span className="block text-[10px] text-gray-400 uppercase font-bold">Website URL</span>
                          <a href={selectedListing.website} target="_blank" rel="noopener noreferrer" className="text-[#2ab391] hover:underline font-bold break-all">
                            {selectedListing.website}
                          </a>
                        </div>
                      )}
                      <div>
                        <span className="block text-[10px] text-gray-400 uppercase font-bold">Address</span>
                        <span className="text-gray-700 font-semibold block leading-relaxed">
                          {selectedListing.address}, {selectedListing.city}, {selectedListing.state} - {selectedListing.zipcode}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Business Details</h4>
                    <div className="space-y-2 bg-gray-50/50 p-4 rounded-xl border border-gray-150/40 text-xs">
                      <div>
                        <span className="block text-[10px] text-gray-400 uppercase font-bold">GSTIN Number</span>
                        <span className="text-gray-800 font-bold">{selectedListing.gstin || 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  {selectedListing.businessHours && (
                    <div>
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Business Hours</h4>
                      <div className="bg-gray-50/55 p-4 rounded-xl border border-gray-150/40 text-xs space-y-2 text-gray-700 font-medium">
                        <div className="text-[10px] text-gray-400 uppercase font-bold mb-1">
                          Mode: <span className="text-gray-750 font-bold uppercase">{selectedListing.businessHours.type.replace(/_/g, ' ')}</span>
                        </div>
                        {selectedListing.businessHours.type === 'ALWAYS_OPEN' && (
                          <div className="text-emerald-600 font-bold">Open 24 hours / 7 days</div>
                        )}
                        {(selectedListing.businessHours.type === 'MON_TO_SAT' || selectedListing.businessHours.type === 'MON_TO_FRI') && selectedListing.businessHours.mainHours && (
                          <div className="space-y-1">
                            <div>
                              Weekdays: <span className="font-bold text-gray-800">{selectedListing.businessHours.mainHours.openTime} - {selectedListing.businessHours.mainHours.closeTime}</span>
                            </div>
                            {selectedListing.businessHours.type === 'MON_TO_FRI' && selectedListing.businessHours.saturdayHours && (
                              <div>
                                Saturday: <span className="font-bold text-gray-800">{selectedListing.businessHours.saturdayHours.openTime} - {selectedListing.businessHours.saturdayHours.closeTime}</span>
                              </div>
                            )}
                            {selectedListing.businessHours.sundayHours && (
                              <div>
                                Sunday: <span className="font-bold text-gray-800">{selectedListing.businessHours.sundayHours.openTime} - {selectedListing.businessHours.sundayHours.closeTime}</span>
                              </div>
                            )}
                          </div>
                        )}
                        {selectedListing.businessHours.type === 'CUSTOM' && selectedListing.businessHours.customHours && selectedListing.businessHours.customHours.length > 0 && (
                          <div className="space-y-1 max-h-32 overflow-y-auto pr-1">
                            {selectedListing.businessHours.customHours.map((ch, idx) => (
                              <div key={idx} className="flex justify-between border-b border-gray-100/50 pb-1 last:border-0 last:pb-0">
                                <span className="text-gray-500 font-semibold">{ch.day}:</span>
                                <span className="font-bold text-gray-800">{ch.openTime} - {ch.closeTime}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {(selectedListing.instagram || selectedListing.linkedin || selectedListing.facebook || selectedListing.twitter) && (
                    <div>
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Social Connect</h4>
                      <div className="flex gap-2.5 flex-wrap">
                        {selectedListing.instagram && (
                          <a href={selectedListing.instagram} target="_blank" rel="noopener noreferrer" className="p-2 bg-gray-50 hover:bg-rose-50 border border-gray-200 hover:border-rose-200 text-gray-655 hover:text-rose-600 rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 cursor-pointer">
                            Instagram
                          </a>
                        )}
                        {selectedListing.linkedin && (
                          <a href={selectedListing.linkedin} target="_blank" rel="noopener noreferrer" className="p-2 bg-gray-50 hover:bg-blue-50 border border-gray-200 hover:border-blue-200 text-gray-655 hover:text-blue-700 rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 cursor-pointer">
                            LinkedIn
                          </a>
                        )}
                        {selectedListing.facebook && (
                          <a href={selectedListing.facebook} target="_blank" rel="noopener noreferrer" className="p-2 bg-gray-50 hover:bg-sky-50 border border-gray-200 hover:border-sky-200 text-gray-655 hover:text-sky-600 rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 cursor-pointer">
                            Facebook
                          </a>
                        )}
                        {selectedListing.twitter && (
                          <a href={selectedListing.twitter} target="_blank" rel="noopener noreferrer" className="p-2 bg-gray-50 hover:bg-slate-105 border border-gray-200 hover:border-slate-350 text-gray-655 hover:text-black rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 cursor-pointer">
                            Twitter
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end border-t border-gray-100 p-6 bg-gray-50/50">
              <button
                onClick={() => setSelectedListing(null)}
                className="px-5 py-2.5 bg-gray-900 hover:bg-black text-white rounded-xl text-sm font-semibold active:scale-[0.98] transition-all cursor-pointer shadow-md shadow-gray-900/10"
              >
                Close Details
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListingApprovalsTab;
